// React hooks for MEXC API integration
import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getMexcApiClient } from '../services/mexcApi';
import { PositionPersistenceService } from '../services/positionPersistence';
import type { Position, CreateOrderRequest } from '../types/mexc';

// Get the 1-minute refresh interval for API calls
const API_REFRESH_INTERVAL = parseInt(import.meta.env.VITE_PRICE_REFRESH_INTERVAL) || 60000; // 1 minute
const POSITION_REFRESH_INTERVAL = parseInt(import.meta.env.VITE_POSITION_REFRESH_INTERVAL) || 30000; // 30 seconds (keep for positions)

// Hook for fetching positions with persistence fallback
export function usePositions() {
  const mexcApi = getMexcApiClient();
  const [persistedPositions, setPersistedPositions] = useState<Position[]>([]);
  const [connectionStatus, setConnectionStatus] = useState(PositionPersistenceService.getConnectionStatus());

  const query = useQuery({
    queryKey: ['positions'],
    queryFn: () => mexcApi.getPositions(),
    refetchInterval: POSITION_REFRESH_INTERVAL, // Keep 30 seconds for positions
    staleTime: POSITION_REFRESH_INTERVAL * 2,
    // Enhanced retry with exponential backoff (handled by API client)
    retry: (failureCount, error) => {
      // Let the API client handle retries, but allow React Query to retry network errors
      const errorResponse = (error as any)?.response;
      if (errorResponse?.data?.code === 513) {
        return failureCount < 2; // Limited retries for auth errors
      }
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Use persistence service to handle fallback logic
  useEffect(() => {
    const result = PositionPersistenceService.getPositionsWithFallback(
      query.data,
      query.isLoading,
      query.error
    );

    setPersistedPositions(result.positions);
    setConnectionStatus(result.connectionStatus);

    console.log('🔄 Position persistence result:', {
      apiPositions: query.data?.length || 0,
      persistedPositions: result.positions.length,
      isUsingCache: result.isUsingCache,
      isConnected: result.connectionStatus.isConnected
    });
  }, [query.data, query.isLoading, query.error]);

  return {
    ...query,
    data: persistedPositions,
    connectionStatus,
    isUsingCache: persistedPositions.length > 0 && (!query.data || query.data.length === 0),
  };
}

// Hook for fetching open orders (align with 1-minute interval)
export function useOpenOrders(symbol?: string) {
  const mexcApi = getMexcApiClient();

  return useQuery({
    queryKey: ['openOrders', symbol],
    queryFn: () => mexcApi.getOpenOrders(symbol),
    refetchInterval: API_REFRESH_INTERVAL, // ✅ Changed to 1 minute
    staleTime: 60000, // 1 minute stale time
  });
}

// Hook for fetching assets (align with 1-minute interval)
export function useAssets() {
  const mexcApi = getMexcApiClient();

  return useQuery({
    queryKey: ['assets'],
    queryFn: () => mexcApi.getAssets(),
    refetchInterval: API_REFRESH_INTERVAL, // ✅ Changed to 1 minute
    staleTime: 60000, // 1 minute stale time
  });
}

// Hook for fetching contract information (keep long cache)
export function useContractInfo(symbol?: string) {
  const mexcApi = getMexcApiClient();

  return useQuery({
    queryKey: ['contractInfo', symbol],
    queryFn: () => mexcApi.getContractInfo(symbol),
    staleTime: 300000, // 5 minutes (unchanged)
    refetchInterval: false, // ✅ Disable auto-refresh for contract info
  });
}

// Hook for fetching all available trading symbols
export function useAvailableSymbols() {
  const mexcApi = getMexcApiClient();

  return useQuery({
    queryKey: ['availableSymbols'],
    queryFn: async () => {
      try {
        // Fetch contract details and tickers in parallel
        const [contractInfo, tickers] = await Promise.all([
          mexcApi.getContractInfo(),
          mexcApi.getTicker()
        ]);

        // Extract symbols from both sources
        const contractSymbols = Array.isArray(contractInfo)
          ? contractInfo.map(c => c.symbol)
          : [];

        const tickerSymbols = Array.isArray(tickers)
          ? tickers.map(t => t.symbol)
          : [];

        // Get unique symbols and sort
        const uniqueSymbols = Array.from(new Set([...contractSymbols, ...tickerSymbols])).sort();

        console.log('✅ Available symbols fetched:', {
          count: uniqueSymbols.length,
          symbols: uniqueSymbols.slice(0, 10) // Log first 10 for debugging
        });

        return uniqueSymbols;
      } catch (error) {
        console.error('❌ Failed to fetch available symbols:', error);
        throw error;
      }
    },
    staleTime: 600000, // 10 minutes - symbols don't change frequently
    refetchInterval: false, // Don't auto-refresh
    retry: 2,
  });
}

// Hook for fetching ticker data (DISABLE auto-refresh, rely on WebSocket + manual refresh)
export function useTicker(symbol?: string) {
  const mexcApi = getMexcApiClient();

  return useQuery({
    queryKey: ['ticker', symbol],
    queryFn: () => mexcApi.getTicker(symbol),
    refetchInterval: false, // ✅ DISABLED - rely on WebSocket + usePriceRefresh
    staleTime: API_REFRESH_INTERVAL, // 1 minute stale time
    gcTime: API_REFRESH_INTERVAL, // Keep in cache for 1 minute
  });
}

// Hook for fetching kline/candlestick data (align with 1-minute interval)
export function useKlineData(
  symbol?: string,
  interval: string = 'Min1',
  start?: number,
  end?: number
) {
  const mexcApi = getMexcApiClient();

  return useQuery({
    queryKey: ['kline', symbol, interval, start, end],
    queryFn: async () => {
      if (!symbol) {
        throw new Error('Symbol is required for kline data');
      }
      console.log('📊 Fetching kline data for:', { symbol, interval, start, end });
      const result = await mexcApi.getKlineData(symbol, interval, start, end);
      console.log('📊 Kline API Response:', result);
      return result;
    },
    enabled: !!symbol,
    staleTime: 300000, // 5 minutes stale time
    refetchInterval: API_REFRESH_INTERVAL, // ✅ Changed to 1 minute
  });
}

// Hook for creating orders
export function useCreateOrder() {
  const queryClient = useQueryClient();
  const mexcApi = getMexcApiClient();

  return useMutation({
    mutationFn: (orderRequest: CreateOrderRequest) => mexcApi.createOrder(orderRequest),
    onSuccess: () => {
      // Invalidate related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['positions'] });
      queryClient.invalidateQueries({ queryKey: ['openOrders'] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

// Hook for canceling orders
export function useCancelOrder() {
  const queryClient = useQueryClient();
  const mexcApi = getMexcApiClient();

  return useMutation({
    mutationFn: (orderId: number) => mexcApi.cancelOrder(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['openOrders'] });
      queryClient.invalidateQueries({ queryKey: ['positions'] });
    },
  });
}

// Hook for updating position TP/SL
export function useUpdatePositionTPSL() {
  const queryClient = useQueryClient();
  const mexcApi = getMexcApiClient();

  return useMutation({
    mutationFn: ({ positionId, stopLossPrice, takeProfitPrice }: {
      positionId: number;
      stopLossPrice?: number;
      takeProfitPrice?: number;
    }) => mexcApi.updatePositionTPSL(positionId, stopLossPrice, takeProfitPrice),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['positions'] });
      queryClient.invalidateQueries({ queryKey: ['stopOrders'] });
    },
  });
}

// Hook for changing leverage
export function useChangeLeverage() {
  const queryClient = useQueryClient();
  const mexcApi = getMexcApiClient();

  return useMutation({
    mutationFn: ({ symbol, leverage, openType }: {
      symbol: string;
      leverage: number;
      openType: 1 | 2;
    }) => mexcApi.changeLeverage(symbol, leverage, openType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['positions'] });
    },
  });
}

// Hook for managing trading state
export function useTradingState() {
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
  const [isTrading, setIsTrading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const positions = usePositions();
  const orders = useOpenOrders(selectedSymbol || undefined);
  const assets = useAssets();

  // Auto-select first position's symbol when positions are loaded and no symbol is selected
  useEffect(() => {
    if (!selectedSymbol && positions.data && positions.data.length > 0) {
      const firstSymbol = positions.data[0].symbol;
      console.log('🎯 Auto-selecting first position symbol:', firstSymbol);
      setSelectedSymbol(firstSymbol);
    }
  }, [selectedSymbol, positions.data]);

  const clearError = useCallback(() => setError(null), []);

  const selectSymbol = useCallback((symbol: string | null) => {
    setSelectedSymbol(symbol);
    clearError();
  }, [clearError]);

  // Enhanced debug logging with connection status (guarded)
  if (import.meta.env.DEV && localStorage.getItem('debug_ui') === 'true') {
    console.log('🔄 Trading state debug:', {
      positionsCount: positions.data?.length || 0,
      isUsingCache: positions.isUsingCache,
      isConnected: positions.connectionStatus?.isConnected,
      consecutiveFailures: positions.connectionStatus?.consecutiveFailures,
      lastError: positions.connectionStatus?.lastError?.message,
      isLoading: positions.isLoading,
      isFetching: positions.isFetching
    });
  }

  return {
    selectedSymbol,
    isTrading,
    error,
    positions: positions.data || [],
    orders: orders.data || [],
    assets: assets.data || [],
    isLoading: positions.isLoading || orders.isLoading || assets.isLoading,
    connectionStatus: positions.connectionStatus,
    isUsingCache: positions.isUsingCache,
    selectSymbol,
    setIsTrading,
    setError,
    clearError,
    refetch: {
      positions: positions.refetch,
      orders: orders.refetch,
      assets: assets.refetch,
    },
  };
}

// Hook for real-time price tracking
export function usePriceTracking(_symbols: string[]) {
  const [prices, setPrices] = useState<Record<string, number>>({});
  const [priceChanges, setPriceChanges] = useState<Record<string, 'up' | 'down' | 'neutral'>>({});

  const updatePrice = useCallback((symbol: string, newPrice: number) => {
    setPrices(prev => {
      const oldPrice = prev[symbol];
      const change = oldPrice ? (newPrice > oldPrice ? 'up' : newPrice < oldPrice ? 'down' : 'neutral') : 'neutral';

      setPriceChanges(prevChanges => ({
        ...prevChanges,
        [symbol]: change
      }));

      // Reset price change indicator after animation
      setTimeout(() => {
        setPriceChanges(prevChanges => ({
          ...prevChanges,
          [symbol]: 'neutral'
        }));
      }, 1000);

      return {
        ...prev,
        [symbol]: newPrice
      };
    });
  }, []);

  return {
    prices,
    priceChanges,
    updatePrice,
  };
}
